import React, { useState, useEffect } from "react";
import { imagesUrls } from "./WebAppsSharedStuffs/utils/constants";
import { useNavigate } from "react-router-dom";
import Footer from "./WebAppsSharedStuffs/components/Footer";
import DonateButton from "./WebAppsSharedStuffs/components/DonateButton";
import Header from "./WebAppsSharedStuffs/components/Header";
import piNetworkAdsManager from "./WebAppsSharedStuffs/utils/PiNetworkAdsManager";

const App = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeGame, setActiveGame] = useState(0);
  const navigate = useNavigate();

  // Games featured on the platform
  const games = [
    {
      name: "Cosmic Racer",
      description:
        "A space racing game where you compete against IA in high-speed races through space.",
      type: "Single Player",
      image: imagesUrls["ai"],
      path: "/game/cosmic-racer",
    },
    {
      name: "Wall Breaker",
      description:
        "Break the wall by bouncing the ball against it. Collect power-ups and avoid obstacles.",
      path: "/game/wall-breaker",
      type: "Single Player",
      image: imagesUrls["SuperLike"],
    },
    {
      name: "Memory Match",
      description:
        "Match tiles to clear the board in this exciting memory game.",
      type: "Single Player",
      image: imagesUrls["arthereum"],
      path: "/game/memory-match",
    },
    {
      name: "Guess the Number",
      description:
        "Try to guess the number selected by the game within a limited number of attempts.",
      type: "Single Player",
      image: imagesUrls["chooseProfile"],
      path: "/game/guess-number",
    },
  ];

  // Testimonials
  const testimonials = [
    {
      text: "I love that I can play with my friends without needing to create accounts or deal with servers. Just share a code and we're gaming together!",
      author: "Alex M.",
      role: "Casual Gamer",
    },
    {
      text: "The game variety is amazing and the no-server approach means I can play anywhere, even with spotty internet.",
      author: "Jordan T.",
      role: "Game Enthusiast",
    },
    {
      text: "As a developer, I appreciate the technical innovation behind this platform. Peer-to-peer gaming is the future!",
      author: "Sam K.",
      role: "Software Developer",
    },
  ];

  // FAQs
  const faqs = [
    {
      question: "How does peer-to-peer multiplayer work?",
      answer:
        "Our platform uses WebRTC technology to establish direct connections between players. You simply share your unique game code with friends, and they can join your game instantly without any servers involved.",
    },
    {
      question: "Do I need to create an account?",
      answer:
        "No! Our platform is designed to be hassle-free. No accounts, no logins, just pure gaming. Your game progress is stored locally on your device.",
    },
    {
      question: "Is my connection secure?",
      answer:
        "Yes, all peer-to-peer connections are encrypted end-to-end, ensuring your gaming sessions remain private and secure.",
    },
    {
      question: "Can I play offline?",
      answer:
        "All single-player games work completely offline. For multiplayer games, you'll need an internet connection only to establish the initial connection with other players.",
    },
  ];

  useEffect(() => {
    // Animation on load
    setIsVisible(true);

    // Game showcase rotation
    const interval = setInterval(() => {
      setActiveGame((prev) => (prev + 1) % games.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);
  const startNow = () => {
    navigate("/home");
  };
  // Function to execute 5 seconds after page load
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     piNetworkAdsManager.handleRewardedAdFlowFront();
  //   }, 2500);
  //   const adTimer = setInterval(async () => {
  //     piNetworkAdsManager.handleRewardedAdFlowFront();
  //   }, 60000);
  //   return () => {
  //     clearTimeout(timer);
  //     clearInterval(adTimer);
  //   };
  // }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        color: "#1a1a2e",
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      {/* Hero Section */}
      <Header />

      {/* Featured Games Section */}
      <section
        id="games"
        style={{
          padding: "100px 5%",
          background: "#f8f9fa",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginBottom: "60px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
              color: "#1a1a2e",
            }}
          >
            Featured Games
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "#555",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Explore our collection of exciting games, from fast-paced
            multiplayer experiences to engaging single-player adventures.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "50px",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          {/* Game Showcase */}
          <div
            style={{
              position: "relative",
              height: "500px",
              overflow: "hidden",
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            }}
          >
            {games.map((game, index) => (
              <div
                key={index}
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  background:
                    "linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)",
                  opacity: index === activeGame ? 1 : 0,
                  transition: "opacity 1s ease",
                  zIndex: index === activeGame ? 1 : 0,
                }}
              >
                <img
                  src={game.image}
                  alt={game.name}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: -1,
                  }}
                />
                <div
                  style={{
                    padding: "5%",

                    maxWidth: "500px",
                    color: "white",
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      padding: "8px 16px",
                      background:
                        game.type === "Multiplayer" ? "#61dafb" : "#ff7d7d",
                      borderRadius: "20px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#1a1a2e",
                      marginBottom: "15px",
                    }}
                  >
                    {game.type}
                  </div>
                  <h3
                    style={{
                      fontSize: "clamp(1.5rem, 3vw, 2.5rem)",
                      fontWeight: "bold",
                      marginBottom: "15px",
                    }}
                  >
                    {game.name}
                  </h3>
                  <p
                    style={{
                      fontSize: "18px",
                      lineHeight: 1.6,
                      marginBottom: "30px",
                    }}
                  >
                    {game.description}
                  </p>
                  <button
                    onClick={() => navigate(game.path)}
                    style={{
                      background: "white",
                      color: "#1a1a2e",
                      border: "none",
                      padding: "12px 24px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                    }}
                  >
                    Play Now
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination Indicators */}
            <div
              style={{
                position: "absolute",
                bottom: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "10px",
                zIndex: 2,
              }}
            >
              {games.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveGame(index)}
                  title={`Show game ${index + 1}`}
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background:
                      index === activeGame ? "white" : "rgba(255,255,255,0.4)",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 0.3s ease",
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Game Grid */}
          <div
            className="animate-on-scroll"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "30px",
              opacity: 0,
              transform: "translateY(20px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            {games.map((game, index) => (
              <div
                key={index}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
              >
                <img
                  src={game.image}
                  alt={game.name}
                  style={{
                    width: "100%",
                    height: "180px",
                    objectFit: "cover",
                  }}
                />
                <div style={{ padding: "20px" }}>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      background:
                        game.type === "Multiplayer"
                          ? "rgba(97, 218, 251, 0.2)"
                          : "rgba(255, 125, 125, 0.2)",
                      color:
                        game.type === "Multiplayer" ? "#0492c2" : "#e63946",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      marginBottom: "10px",
                    }}
                  >
                    {game.type}
                  </div>
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      marginBottom: "10px",
                    }}
                  >
                    {game.name}
                  </h3>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#555",
                      lineHeight: 1.5,
                      marginBottom: "20px",
                    }}
                  >
                    {game.description}
                  </p>
                  <button
                    style={{
                      background: "#f0f0f0",
                      color: "#1a1a2e",
                      border: "none",
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "background 0.2s",
                    }}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        style={{
          padding: "100px 5%",
          background: "white",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginBottom: "60px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
              color: "#1a1a2e",
            }}
          >
            Why Choose Our Platform
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "#555",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            We've built a gaming experience with modern technology and player
            freedom at its core.
          </p>
        </div>

        <div
          className="animate-on-scroll"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "40px",
            maxWidth: "1200px",
            margin: "0 auto",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "30px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "rgba(97, 218, 251, 0.1)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#61dafb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "15px",
              }}
            >
              No Subscription Fees
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#555",
                lineHeight: 1.6,
              }}
            >
              All games are completely free to play. No hidden fees, no premium
              subscriptions, just unlimited gaming.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "30px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "rgba(97, 218, 251, 0.1)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#61dafb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "15px",
              }}
            >
              Play Anywhere
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#555",
                lineHeight: 1.6,
              }}
            >
              Our platform works on any device with a modern browser. Play on
              desktop, tablet, or mobile with no downloads required.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "30px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "rgba(97, 218, 251, 0.1)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#61dafb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "15px",
              }}
            >
              Private Multiplayer
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#555",
                lineHeight: 1.6,
              }}
            >
              Peer-to-peer connections keep your gaming sessions private. No
              servers ever see or store your gameplay data.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id="how-it-works"
        style={{
          padding: "100px 5%",
          background: "#f8f9fa",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginBottom: "60px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
              color: "#1a1a2e",
            }}
          >
            How It Works
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "#555",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Three simple steps to start playing with friends, no servers
            required.
          </p>
        </div>

        <div
          className="animate-on-scroll"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "80px",
            maxWidth: "1000px",
            margin: "0 auto",
            position: "relative",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          {/* Timeline connector */}
          <div
            style={{
              position: "absolute",
              left: "calc(50% - 1px)",
              top: "0",
              bottom: "0",
              width: "2px",
              background: "#e0e0e0",
              display: "block",
            }}
          ></div>

          {/* Step 1 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "5%",
            }}
          >
            <div
              style={{
                flex: 1,
                padding: "30px",
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#61dafb",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  1
                </span>
                Choose A Game
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  color: "#555",
                  lineHeight: 1.6,
                }}
              >
                Browse our collection of games and select the one you want to
                play. We offer everything from puzzles to racing games to
                strategy titles.
              </p>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={imagesUrls["computer"]}
                alt="Choose a game"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                }}
              />
            </div>
          </div>

          {/* Step 2 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "5%",
              flexDirection: "row-reverse",
            }}
          >
            <div
              style={{
                flex: 1,
                padding: "30px",
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#61dafb",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  2
                </span>
                Generate Your Code
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  color: "#555",
                  lineHeight: 1.6,
                }}
              >
                For multiplayer games, you'll receive a unique connection code.
                Share this code with your friends so they can join your game
                directly.
              </p>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={imagesUrls["trades"]}
                alt="Generate code"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                }}
              />
            </div>
          </div>

          {/* Step 3 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "5%",
            }}
          >
            <div
              style={{
                flex: 1,
                padding: "30px",
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#61dafb",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  3
                </span>
                Play Together
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  color: "#555",
                  lineHeight: 1.6,
                }}
              >
                Once your friends enter your code, you're connected directly to
                each other. No middle-man servers, just pure, low-latency
                gameplay between players.
              </p>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={imagesUrls["tell"]}
                alt="Play together"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        style={{
          padding: "100px 5%",
          background:
            "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
          color: "white",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginBottom: "60px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            What Our Users Say
          </h2>
          <p
            style={{
              fontSize: "18px",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Join thousands of happy players who love our serverless gaming
            experience.
          </p>
        </div>

        <div
          className="animate-on-scroll"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "30px",
            maxWidth: "1200px",
            margin: "0 auto",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              style={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                padding: "30px",
                borderRadius: "12px",
                position: "relative",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="rgba(97, 218, 251, 0.3)"
                stroke="none"
              >
                <path d="M10,7L8,11L11,11L9,15M14,7L12,11L15,11L13,15" />
              </svg>
              <p
                style={{
                  fontSize: "16px",
                  lineHeight: 1.6,
                  marginBottom: "20px",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                "{testimonial.text}"
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    background: "#61dafb",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#1a1a2e",
                  }}
                >
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <p
                    style={{
                      fontWeight: "bold",
                      fontSize: "18px",
                      margin: 0,
                    }}
                  >
                    {testimonial.author}
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "rgba(255,255,255,0.7)",
                      margin: 0,
                    }}
                  >
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        style={{
          padding: "100px 5%",
          background: "white",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            textAlign: "center",
            marginBottom: "60px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
              color: "#1a1a2e",
            }}
          >
            Frequently Asked Questions
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "#555",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Everything you need to know about our platform and how it works.
          </p>
        </div>

        <div
          className="animate-on-scroll"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          {faqs.map((faq, index) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const [isOpen, setIsOpen] = useState(index === 0);

            return (
              <div
                key={index}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
              >
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "20px",
                    background: "white",
                    border: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#1a1a2e",
                  }}
                >
                  {faq.question}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                <div
                  style={{
                    padding: isOpen ? "0 20px 20px" : "0 20px",
                    maxHeight: isOpen ? "200px" : "0",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#555",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          padding: "100px 5%",
          background:
            "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <div
          className="animate-on-scroll"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            Ready to Play Without Limits?
          </h2>
          <p
            style={{
              fontSize: "18px",
              lineHeight: 1.6,
              marginBottom: "40px",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Join thousands of players enjoying serverless gaming today. No
            accounts, no downloads, just pure gaming fun.
          </p>
          <button
            onClick={startNow}
            style={{
              background: "#61dafb",
              color: "#1a1a2e",
              border: "none",
              padding: "18px 36px",
              fontSize: "18px",
              fontWeight: "bold",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: "0 4px 12px rgba(97, 218, 251, 0.25)",
            }}
          >
            Start Playing Now
          </button>
        </div>
      </section>

      <Footer />

      {/* CSS Animations */}
      <style>{`
        @keyframes bounce {
          0%,
          20%,
          50%,
          80%,
          100% {
            transform: translateY(0) translateX(-50%);
          }
          40% {
            transform: translateY(-20px) translateX(-50%);
          }
          60% {
            transform: translateY(-10px) translateX(-50%);
          }
        }

        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
};

export default App;
